import { DataSourceInfo, Header, ModelInfo } from "build-ts/metadata";
import showToast from "../hooks/useCustomToast";
import { KGColumnProto } from "build-ts/knowledege_graph";
import { DependencyRequestProto, GetKGMapping_Request, GetKGMapping_Response } from "build-ts/interface";
import { DependencyProto } from "build-ts/interface";
import {
  getAllKgColumnsUrl,
  getColumnValuesUrl,
  getDataModelInfoUrl,
  getDataModelCreateUrl,
  getDataModelUpdateUrl,
  getDataModelUrl,
  getKgColumnUrl,
  getKgColumnValuesUrl,
  getListDataModelUrl,
  getMatchingKgColumnsUrl,
  getMetadataDependencyUrl,
  putKgColumnUrl,
  getSyncTsWorksheetUrl,
  updateKgColumnUrl,
} from "./newtonProperties";

/**
 * Sends a request to create a new model.
 *
 * @param {ModelInfo} model_info - The updated model information.
 * @returns {Promise<ModelInfo>} A promise that resolves to the API response.
 * @throws {Error} Throws an error if the request fails.
 */
export const createModel = async (model_info: ModelInfo) => {
  try {
    const response = await fetch(getDataModelCreateUrl(), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(ModelInfo.toJSON(model_info)),
    });

    if (!response.ok) {
      showToast("Error creating data model", "", "error");
      return null;
    }
    const data = await response.json();
    return ModelInfo.fromJSON(data);
  } catch (error) {
    showToast("Error sending data to API", "", "error");
  }
};

/**
 * Sends a request to update a new model.
 * @param {UpdatedModelInfo} model_info - The updated model information.
 * @returns {Promise<ModelInfo>} A promise that resolves to the API response.
 * @throws {Error} Throws an error if the request fails.
 */
export const updateModel = async (model_info: ModelInfo) => {
  try {
    const response = await fetch(getDataModelUpdateUrl(), {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(ModelInfo.toJSON(model_info)),
    });

    if (!response.ok) {
      showToast("Error updating model", "", "error");
      return null;
    }
    const data = await response.json();
    return ModelInfo.fromJSON(data);
  } catch (error) {
    showToast("Error sending data to API", "", "error");
  }
};

/**
 * Fetches the list of models.
 *
 * @returns {Promise<Header[]>} A promise that resolves to the list of models.
 * @throws {Error} Throws an error if the request fails.
 */
export const getDataModelHeaders = async (targetModels: boolean) => {
  try {
    const response = await fetch(`${getListDataModelUrl()}?targetModels=${targetModels}`, {
      credentials: "include",
    });

    if (!response.ok) {
      showToast("Error fetching model list", "", "error");
      return null;
    }

    const data = await response.json();
    return data.map((header: any) => Header.fromJSON(header));
  } catch (error) {
    showToast("Error fetching model list", "", "error");
  }
};

/**
 * Fetches model information based on the provided model source.
 *
 * @param {DataSourceInfo} modelSource - The model source information.
 * @returns {Promise<ModelInfo>} A promise that resolves to the model information.
 * @throws {Error} Throws an error if the request fails.
 */
export const fetchModelInfo = async (modelSource: DataSourceInfo) => {
  try {
    const encodedModelSource = encodeURIComponent(JSON.stringify(DataSourceInfo.toJSON(modelSource)));
    const response = await fetch(`${getDataModelInfoUrl()}?dataSourceJson=${encodedModelSource}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    });

    if (!response.ok) {
      showToast("Error fetching model Info", "", "error");
      return null;
    }
    const data = await response.json();
    return ModelInfo.fromJSON(data);
  } catch (error) {
    showToast("Error fetching data", "", "error");
  }
};

/**
 * Fetches model information based on the provided model ID.
 *
 * @param {string} id - The ID of the model to fetch.
 * @returns {Promise<ModelInfo>} A promise that resolves to the model information.
 * @throws {Error} Throws an error if the request fails.
 */
export const getModelById = async (id: string) => {
  try {
    const response = await fetch(`${getDataModelUrl()}/${id}`, {
      credentials: "include",
    });

    if (!response.ok) {
      showToast("Error fetching model by ID", "", "error");
      return null;
    }

    const data = await response.json();
    return ModelInfo.fromJSON(data);
  } catch (error) {
    console.error("Error fetching model by ID:", error);
    showToast("Error fetching model by ID", "", "error");
  }
};

export const getColumnValues = async (id: string, kgColumnId?: string | null) => {
  try {
    // Build base URL
    let url = `${getColumnValuesUrl()}?logicalColumnId=${id}`;

    // Add kgColumnId only if it's not null or undefined
    if (kgColumnId) {
      url += `&kgColumnId=${kgColumnId}`;
    }

    const response = await fetch(url, {
      method: "GET",
      credentials: "include",
    });

    if (!response.ok) {
      return null;
    }
    return await response.json();
  } catch (error) {
    showToast("Error in fetching column values", "", "error");
  }
};

/**
 * Delete Model.
 *
 * @returns {Promise<any>} A promise that resolves to the API response.
 * @throws {Error} Throws an error if the request fails.
 */
export const deleteModel = async (modelId: string) => {
  try {
    const response = await fetch(`${getDataModelUrl()}/${modelId}`!, {
      method: "DELETE",
      credentials: "include",
    });
    if (response.status !== 204) {
      showToast("Dependency found", "", "error");
      return await response.json();
    }
    showToast("Model deleted successfully", "", "success");
    return response;
  } catch (error) {
    showToast("Error in deleting Model", "", "error");
  }
};

/**
 *
 * Fetches the list of all KG columns.
 * @returns {Promise<Header[]>} A promise that resolves to the list of all KG columns.
 * @throws {Error} Throws an error if the request fails.
 */
export const getAllKGColumns = async () => {
  try {
    const response = await fetch(`${getAllKgColumnsUrl()}`, {
      credentials: "include",
    });

    if (!response.ok) {
      showToast("Error fetching all KG columns", "", "error");
      return null;
    }

    const data = await response.json();
    return data.map((header: any) => Header.fromJSON(header));
  } catch (error) {
    showToast("Error fetching all KG columns", "", "error");
  }
};

/**
 * Saves a KG column.
 *
 * @param {KGColumnProto} kgColumn - The KG column to save.
 * @returns {Promise<KGColumnProto>} A promise that resolves to the list of all KG columns.
 * @throws {Error} Throws an error if the request fails.
 */
export const saveKGColumn = async (kgColumn: KGColumnProto, updateLogicalMappings: boolean) => {
  try {
    const response = await fetch(`${putKgColumnUrl()}?updateLogicalMappings=${updateLogicalMappings}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(KGColumnProto.toJSON(kgColumn)),
    });

    if (!response.ok) {
      showToast("Error saving KG column", "", "error");
      return null;
    }

    const data = await response.json();
    return KGColumnProto.fromJSON(data);
  } catch (error) {
    showToast("Error saving KG column", "", "error");
  }
};

/**
 * Updates a KG column.
 *
 * @param {KGColumnProto} kgColumn - The KG column to update.
 * @param {string} kgColumnId - The ID of the KG column to update.
 * @param {string[]} addMappings - The logical column IDs to add as mappings.
 * @param {string[]} removeMappings - The logical column IDs to remove as mappings.
 * @param {boolean} updateLogicalMappings - If true, update the logical mappings.
 * @returns {Promise<KGColumnProto>} A promise that resolves to the updated KG column.
 * @throws {Error} Throws an error if the request fails.
 */
export const updateKGColumn = async (
  kgColumn?: KGColumnProto | null,
  kgColumnId?: string,
  addMappings?: string[],
  removeMappings?: string[],
  updateLogicalMappings?: boolean
) => {
  try {
    let response: Response;

    if (kgColumnId) {
      // Case 1: Mapping updates only - send id parameter with empty JSON body
      const qs =
        `id=${kgColumnId}` +
        `&addMappings=${encodeURIComponent(JSON.stringify(addMappings || []))}` +
        `&removeMappings=${encodeURIComponent(JSON.stringify(removeMappings || []))}` +
        `&updateColumnMapping=${updateLogicalMappings || false}`;
      response = await fetch(`${updateKgColumnUrl()}?${qs}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });
    } else {
      response = await fetch(`${updateKgColumnUrl()}?updateColumnMapping=${updateLogicalMappings || false}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(KGColumnProto.toJSON(kgColumn || KGColumnProto.create({}))),
      });
    }

    if (!response.ok) {
      showToast("Error updating KG column", "", "error");
      return null;
    }
    const data = await response.json();
    return KGColumnProto.fromJSON(data);
  } catch (error) {
    showToast("Error updating KG column", "", "error");
    return null;
  }
};

/**
 * Fetches the values for a KG column.
 *
 * @param {string} kgColumnId - The ID of the KG column.
 * @returns {Promise<string[]>} A promise that resolves to the list of KG column values.
 * @throws {Error} Throws an error if the request fails.
 */
export const getKGColumnValues = async (kgColumnId: string) => {
  try {
    const response = await fetch(`${getKgColumnValuesUrl()}?kGColumnId=${kgColumnId}`, {
      method: "GET",
      credentials: "include",
    });

    if (!response.ok) {
      showToast("Error fetching KG column values", "", "error");
      return null;
    }

    return await response.json();
  } catch (error) {
    showToast("Error fetching KG column values", "", "error");
    return null;
  }
};

/**
 * Fetches KG column information based on the provided KG column ID.
 *
 * @param {string} id - The ID of the KG column to fetch.
 * @returns {Promise<KGColumnProto>} A promise that resolves to the KG column information.
 * @throws {Error} Throws an error if the request fails.
 */
export const getKGColumnById = async (id: string, includeColumnMap: boolean) => {
  try {
    const response = await fetch(`${getKgColumnUrl()}/${id}?includeColumnMap=${includeColumnMap}`, {
      credentials: "include",
    });

    if (!response.ok) {
      showToast("Error fetching KG column by ID", "", "error");
      return null;
    }

    const data = await response.json();
    return KGColumnProto.fromJSON(data);
  } catch (error) {
    console.error("Error fetching KG column by ID:", error);
    showToast("Error fetching KG column by ID", "", "error");
  }
};

/**
 * Fetches matching KG columns based on column name and type.
 *
 * @param {string} request - The name and type of the column to match.
 * @returns {Promise<Header[]>} A promise that resolves to the list of matching KG columns.
 * @throws {Error} Throws an error if the request fails.
 */
export const getMatchingKGColumns = async (request: GetKGMapping_Request[]) => {
  try {
    const response = await fetch(`${getMatchingKgColumnsUrl()}`, {
      credentials: "include",
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(request.map(request => GetKGMapping_Request.toJSON(request))),
    });

    if (!response.ok) {
      showToast("Error fetching matching KG columns", "", "error");
      return null;
    }

    const data = await response.json();
    return data.map((header: any) => GetKGMapping_Response.fromJSON(header));
  } catch (error) {
    showToast("Error fetching matching KG columns", "", "error");
    return null;
  }
};

/**
 * Syncs an external data model with the system. Currently supports ThoughtSpot worksheets.
 *
 * @param {string} dataModelId - Optional ID of existing data model to update.
 * @param {string} dataSourceType - The type of external data source.
 * @param {string} sourceModelId - The ID of the external data model to sync.
 * @param {string} includeDiffInResponse - Flag to indicate whether to include column diff in response or not.
 * @returns {Promise<ModelInfo | null>} A promise that resolves to the synced model information.
 * @throws {Error} Throws an error if the request fails.
 */
export const syncDataModel = async (
  dataModelId?: string | null,
  dataSourceType?: string | null,
  sourceModelId?: string | null,
  includeDiffInResponse?: boolean
) => {
  try {
    // Build the URL with query parameters
    let url = `${getSyncTsWorksheetUrl()}?`;
    // Add optional dataModelId if provided
    if (dataModelId) {
      url += `dataModelId=${encodeURIComponent(dataModelId)}`;
    } else if (dataSourceType && sourceModelId) {
      url += `dataSourceType=${encodeURIComponent(dataSourceType)}&sourceModelId=${encodeURIComponent(sourceModelId)}`;
    }

    if (includeDiffInResponse) {
      url += "&includeDiffInResponse=true";
    }

    const response = await fetch(url, {
      method: "GET",
      credentials: "include",
    });

    if (!response.ok) {
      showToast("Error syncing data model", "", "error");
      return null;
    }

    const data = await response.json();
    return {
      modelInfo: ModelInfo.fromJSON(data),
      columnsAdded: data?.columnsAdded,
      columnsRemoved: data?.columnsRemoved,
    };
  } catch (error) {
    showToast("Error syncing data model", "", "error");
    return null;
  }
};

/**
 * Fetches the dependency of the given metadataIds.
 *
 * @param {string[]} request - The dependency request
 */
export const getMetadataDependency = async (request: DependencyRequestProto) => {
  try {
    const response = await fetch(`${getMetadataDependencyUrl()}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(DependencyRequestProto.toJSON(request)),
    });

    if (!response.ok) {
      showToast("Error fetching metadata dependency", "", "error");
      return null;
    }

    const data = await response.json();
    return data.map((header: any) => DependencyProto.fromJSON(header));
  } catch (error) {
    showToast("Error fetching metadata dependency", "", "error");
    return null;
  }
};
